import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { SyncOutlined } from '@ant-design/icons'
import { Button, Divider, Space } from 'antd'
import { DeleteModal, IconFont, SvgIcon, FilterFormSelect } from '@/components'
import { t } from '@/i18n'
import {
  useDeleteTask,
  useDeleteTaskExec,
  useGetBizAreaList,
  useGetGroupList,
  useGetSecurityDeviceList,
  useGetSecurityTarget,
} from './utils'
import TaskTable from '@/components/task-table/task-table'
import DetailDrawer from './detail-drawer'
import { RetestModal } from './retest-modal'
import { CreateReport } from '@/components/create-report'
import { useAddReport } from '@/pages/evaluation/report-task/utils'
import { useTableColumns } from './components/TableColumns'
import { useTaskData } from './hooks/useTaskData'
import { useTaskHandlers } from './hooks/useTaskHandlers'
import { useTaskModals } from './hooks/useTaskModals'
import { buildDataSource, buildSubDataSource } from './utils/dataBuilders'
import { Menu } from 'antd'

export interface CSDataType {
  // 首次运行时间
  firstExecuteTime: string
  // 任务分组
  groupName: string
  // 任务id
  id: string
  // 执行类型
  kind: KindProps
  // 最近结束时间
  latestExecuteTime: string
  // 任务名称
  name: string
  // 任务综合评分
  score: number
  // 状态分布
  statusList: { status: string; count: number }[]
  // 子任务id
  subRowKeys: string[]
  // 优先级
  priority: number
  // 目标主体
  email: string
  // 设备
  devices: { deviceType: string; deviceName: string; deviceIp: string; isOnline: boolean }[]
}

export interface CSSubDataType {
  //  客户端
  agent: string
  //  安全域
  areaName: string
  //  用例发送数
  resourceSendTotal: number
  //  用例总数
  resourceTotal: number
  //  创建时间
  createTime: string
  //  开始时间
  startTime: string
  //  结束时间
  endTime: string
  //  任务执行id
  id: string
  //  任务评分
  score: number
  // 执行状态
  status: string
  // 离线验证任务名称
  taskName?: string
  // 是否可以删除
  isRemove: boolean
}

type KindProps = 'TaskKindImmediately' | 'TaskKindBooking' | 'TaskKindCycle'

const TaskListMain: React.FC = () => {
  // 使用自定义hooks
  const taskData = useTaskData()
  const taskHandlers = useTaskHandlers()
  const taskModals = useTaskModals()

  // 获取筛选数据
  const [area, getArea] = useGetBizAreaList()
  const [getGroupList, groupList] = useGetGroupList()
  const [getSecurityDeviceList, securityDeviceList] = useGetSecurityDeviceList()
  const [getSecurityTarget, securityTarget] = useGetSecurityTarget()

  // 删除和报表相关
  const [deleteTask, deleteTaskLoading] = useDeleteTask()
  const [deleteSubTask, deleteSubTaskLoading] = useDeleteTaskExec()
  const [createReport, createLoading] = useAddReport()

  // 状态管理
  const [searchParams, setSearchParams] = useState({ page: 1, limit: 10, priority: undefined })
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [selectedSubRowKeys, setSelectedSubRowKeys] = useState<string[]>([])
  const [taskFormValue, setTaskFormValue] = useState<{
    field: string | number
    value: string | number | string[] | number[] | undefined
  }>({
    field: '',
    value: '',
  })
  const [groupSelectOpen, setGroupSelectOpen] = useState(false)

  const options = [
    {
      label: t('主任务名称'),
      value: 'search',
      type: 'input' as const,
    },
    {
      label: t('子任务名称'),
      value: 'resultName',
      type: 'input' as const,
    },
    {
      label: t('安全设备'),
      value: 'deviceIds',
      mode: 'multiple' as const,
      type: 'select' as const,
      options: securityDeviceList.map(item => ({
        label: `${item.name || '-'}|${item.deviceType || '-'}|${item.deviceIp || '-'}`,
        value: item.id,
      })),
    },
    {
      label: t('目标主体'),
      value: 'hostIds',
      type: 'select' as const,
      // mode: 'multiple' as const,
      options: securityTarget.map(item => ({
        label: `${item.hostName}|${item.hostIp}`,
        value: item.hostId,
        raw: {
          hostName: item.hostName,
          hostIp: item.hostIp,
          hostId: item.hostId,
        },
      })),
    },
    {
      label: t('安全域'),
      value: 'areaName',
      mode: 'multiple' as const,
      type: 'select' as const,
      options: area.map(item => ({
        label: item.name || t('默认安全域'),
        value: item.name || t('默认安全域'),
      })),
    },
  ]

  // 构建菜单项
  const buildItems = (id: string, isRemove: boolean) => {
    const items = [
      {
        key: 'remove',
        label: (
          <div
            className={isRemove ? 'remove-btn' : 'remove-btn-disabled'}
            onClick={() => isRemove && taskModals.removeData(id, 'main')}
          >
            <IconFont type="icon-shanchu-m" />
            <span className="text">{t('删除')}</span>
          </div>
        ),
      },
    ]
    return <Menu items={items} />
  }

  const buildSubItems = (
    taskName: string,
    id: string,
    taskId: string,
    _status: string,
    isReport: boolean,
    isRemove: boolean,
    isRetest: boolean,
  ) => {
    const items = [
      {
        key: 'report',
        label: (
          <div
            className={isReport ? 'report-btn' : 'report-btn-disabled'}
            onClick={() => isReport && taskModals.reportData(id, taskName)}
          >
            <span className="text">{t('生成报表')}</span>
          </div>
        ),
        disabled: !isReport,
      },
      {
        key: 'retest',
        label: (
          <div
            className={isRetest ? 'download-btn' : 'download-btn-disabled'}
            onClick={() =>
              isRetest &&
              taskModals.setRetestVisible({ visible: true, result_id: id, task_id: taskId })
            }
          >
            <span className="text">{t('任务复测')}</span>
          </div>
        ),
        disabled: !isRetest,
      },
      {
        key: 'remove',
        label: (
          <div
            className={isRemove ? 'remove-btn' : 'remove-btn-disabled'}
            onClick={() => isRemove && taskModals.removeData(id, 'sub')}
          >
            <span className="text">{t('删除')}</span>
          </div>
        ),
        disabled: !isRemove,
      },
    ]
    return <Menu items={items} />
  }

  // 使用表格列定义hook
  const { columns, subColumns } = useTableColumns({
    groupList,
    groupSelectOpen,
    setGroupSelectOpen,
    buildItems,
    buildSubItems,
    handleOpenTask: (id: string, taskStatus: number) => {
      taskHandlers?.handleOpenTask?.(id, taskStatus, () => loadTableData(id))
    },
    viewData: taskModals.viewData,
    handleStopTask: (id: string, taskId: string) => {
      taskHandlers?.handleStopTask?.(id, taskId, () => initTableData(taskId, 1, true))
    },
    setSearchParams: (params: Record<string, any>) => setSearchParams(params),
    searchParams,
  })

  // 数据加载函数
  const loadTableData = async (id: string) => {
    try {
      const res = await taskData.getTaskList(searchParams)
      taskData.setTotal(res.data.total)
      let resp
      if (taskData.count !== 1) {
        resp = await taskData.getTaskExecuteList(id, { ...searchParams, page: 1 })
      } else {
        resp = await taskData.getTaskExecuteList(id, { ...searchParams, page: taskData.count })
      }
      taskData.setSubTotal(resp.data.total)
      taskData.setSubDataSource(buildSubDataSource(resp.data.list, id))
      const subRowKeys = buildSubDataSource(resp.data.list, id)
        .filter(item => item.isRemove)
        .map(v => v.id)
      taskData.setDataSource(buildDataSource(res.data.list, id, subRowKeys))
      setExpandedRowKeys([id])
    } catch (error) {
      console.log(error)
    }
  }

  // 删除任务
  const handleDelete = async () => {
    try {
      if (taskModals.remove.type === 'main') {
        await deleteTask(taskModals.remove.id)
      } else if (taskModals.remove.type === 'sub') {
        await deleteSubTask({ result_id_list: taskModals.remove.id })
      } else {
        await deleteTask(selectedRowKeys)
        if (selectedSubRowKeys.length > 0) {
          await deleteSubTask({ result_id_list: selectedSubRowKeys })
        }
      }
      taskModals.setRemove({ visible: false, id: [], type: '' })
      setSelectedRowKeys([])
      setSelectedSubRowKeys([])
      setSearchParams({ ...searchParams, page: 1, limit: 10 })
    } catch (error) {
      console.log(error)
    }
  }

  // 展开事件
  const handleExpand = ({ expanded, record }) => {
    if (expanded) {
      loadTableData(record.id)
      taskData.setMainTaskName(record.name)
      setExpandedRowKeys([record.key])
      taskData.setCount(1)
    } else {
      setExpandedRowKeys([])
      taskData.setSubDataSource([])
      setSelectedSubRowKeys([])
      taskData.setCount(1)
    }
  }

  // 创建报表
  const confirmAddGroup = values => {
    let taskDataList: { task_name: string; campaign_id: string }[] = []
    if (taskModals.report.multiple) {
      taskDataList = values.campaign_ids.reduce((acc, id) => {
        const item = taskModals.report.selectItem.find(i => i.id === id)
        if (item) {
          acc.push({ task_name: item.task_name, campaign_id: item.id })
        }
        return acc
      }, [])
    } else {
      if (taskModals.report.taskList) {
        taskDataList = taskModals.report.taskList
      }
    }

    createReport({
      task_list: taskDataList.map(item => ({
        ...item,
        campaign_id: item.campaign_id,
      })),
      report_name: values.report_name,
      template_type:
        taskModals.report?.multiple && taskModals.report?.selectItem?.length > 1
          ? [2]
          : values.template_type,
      campaign_type: values.campaign_type,
      emails: '',
      assets: 0,
      include_device_logs: values.include_device_logs,
    })
      .then(() => {
        window.open('/report/task/task-report?tab=11', '_self')
      })
      .catch(_ => {})
  }

  // 初始化表格数据
  const initTableData = async (taskId?: string, page?: number, onlyRefresh?: boolean) => {
    try {
      const res = await taskData.getTaskList(searchParams)
      taskData.setTotal(res.data.total)
      if (res.data.total === 0) return taskData.setDataSource([])
      if (!taskData.mainTaskName) {
        taskData.setMainTaskName(res.data.list?.[0]?.name)
      }
      const resp = await taskData.getTaskExecuteList(taskId || res.data.list?.[0]?.id, {
        ...searchParams,
        page: onlyRefresh ? 1 : page || taskData.count,
      })
      taskData.setSubDataSource(
        buildSubDataSource(resp.data.list, taskId || res.data.list?.[0]?.id),
      )
      taskData.setSubTotal(resp.data.total)
      const subRowKeys = buildSubDataSource(resp.data.list, taskId || res.data.list?.[0]?.id)
        .filter(item => item.isRemove)
        .map(v => v.id)
      taskData.setDataSource(
        buildDataSource(res.data.list, taskId || res.data.list?.[0]?.id, subRowKeys),
      )

      if (onlyRefresh) return
      if (resp.data.list?.length > 0) {
        setExpandedRowKeys([taskId || res.data.list?.[0]?.id])
      } else {
        setExpandedRowKeys([])
      }
    } catch (error) {
      console.log(error)
    }
  }

  // 加载更多子任务数据
  const loadMoreSubData = async (id: string) => {
    taskData.setCount(origin => origin + 1)
    const resp = await taskData.getTaskExecuteList(id, {
      ...searchParams,
      page: taskData.count + 1,
    })
    taskData.setSubDataSource(origin => [...origin, ...buildSubDataSource(resp.data.list, id)])
    const subRowKeys = buildSubDataSource(resp.data.list, id)
      .filter(item => item.isRemove)
      .map(v => v.id)
    taskData.setDataSource(origin => [...buildDataSource(origin, id, subRowKeys, true)])
  }

  useEffect(() => {
    taskData.setCount(1)
    initTableData(undefined, 1)

    return () => {
      taskData.setTotal(0)
      taskData.setDataSource([])
      taskData.setSubDataSource([])
    }
  }, [searchParams])

  useEffect(() => {
    if (taskFormValue?.field) {
      if (taskFormValue.field === 'hostIds') {
        setSearchParams(prev => ({
          ...prev,
          'hostInfo.hostName': taskFormValue?.raw?.hostName,
          'hostInfo.hostIp': taskFormValue?.raw?.hostIp,
          'hostInfo.hostId': taskFormValue?.raw?.hostId,
          page: 1,
        }))
        return
      }
      const selectedOption = options.find(opt => opt.value === taskFormValue.field)
      if (selectedOption) {
        // 只有当 value 有值时才更新 searchCaseParams

        setSearchParams(prev => {
          if (prev[taskFormValue.field] === taskFormValue.value) {
            return prev
          }

          // 如果值为空字符串或空数组，说明是切换了字段，需要清除所有旧的筛选参数
          if (
            taskFormValue.value === '' ||
            (Array.isArray(taskFormValue.value) && taskFormValue.value.length === 0)
          ) {
            // 只保留分页相关参数
            return {
              page: 1,
              limit: prev.limit,
              search: undefined,
              resultName: undefined,
              groupId: undefined,
              deviceIds: undefined,
              hostIds: undefined,
              areaName: undefined,
              sortField: undefined,
              sortOrder: undefined,
            }
          }
          return {
            ...prev,
            page: 1,
            [taskFormValue.field]: taskFormValue.value,
          }
        })
      }
    }
  }, [taskFormValue])

  useEffect(() => {
    getArea().catch(_ => {})
    getGroupList().catch(_ => {})
    getSecurityDeviceList().catch(_ => {})
    getSecurityTarget().catch(_ => {})
  }, [])

  const handleTableChange = async (pagination: any, filters: any, sorter: any) => {
    console.log('sorter', sorter)
    try {
      const params = {
        ...searchParams,
        page: pagination.current,
        limit: pagination.pageSize,
      }

      // 处理主表格筛选
      if (filters) {
        Object.keys(filters).forEach(key => {
          if (filters[key] && filters[key].length > 0) {
            params[key] = filters[key]
          } else {
            delete params[key]
          }
        })
      }

      // 处理主表格排序
      if (sorter && sorter.field) {
        params.sortField =
          sorter.field === 'score'
            ? 'sort_field_score'
            : sorter.field === 'firstExecuteTime'
            ? 'sort_field_update_time'
            : 'sort_field_priority'
        params.sortOrder =
          sorter.order === 'ascend'
            ? 'SORT_ORDER_ASCENDING'
            : sorter.order === 'descend'
            ? 'SORT_ORDER_DESCENDING'
            : undefined
      } else {
        delete params.sortField
        delete params.sortOrder
      }
      if (params.sortField && !params.sortOrder) {
        delete params.sortField
      }
      setSearchParams(params)
    } finally {
    }
  }

  const batchOperation = (
    <Space split={<Divider type={'vertical'} />}>
      <div>{t('批量操作')}</div>
      <div className="batch-delete-btn" onClick={() => taskModals.removeData([], 'all')}>
        <SvgIcon name={'batch_remove'} className="icon-delete" />
        <span>{t('批量删除')}</span>
      </div>
      {selectedRowKeys.length > 1 ? (
        <></>
      ) : (
        selectedSubRowKeys.length > 0 && (
          <div
            className={
              selectedSubRowKeys.length > 5 ? 'batch-report-btn disabled' : 'batch-report-btn'
            }
            onClick={() => {
              if (selectedSubRowKeys.length > 5) return
              const selectItem = [
                ...new Map(
                  taskData.subDataSource
                    .filter(item => selectedSubRowKeys.includes(item.id))
                    .map(v => [
                      v.id,
                      {
                        ...v,
                        task_name: v.taskName,
                        result_status:
                          (v.status as any)?.label === t('已完成')
                            ? 3
                            : (v.status as any)?.label === t('中断')
                            ? 4
                            : 7,
                        executed_plugin_num: v.resourceSendTotal,
                      },
                    ]),
                ).values(),
              ]

              taskModals.setReport({
                visible: true,
                multiple: true,
                name: '',
                id: selectedSubRowKeys,
                selectItem,
              })
            }}
          >
            <SvgIcon
              name={selectedSubRowKeys.length > 0 ? '批量生成报表' : 'batch_remove_ash'}
              className="icon-export"
            />
            <span>{t('批量生成报表')}</span>
          </div>
        )
      )}
    </Space>
  )

  return (
    <Wrapper>
      {/* 标题 */}
      <div className="title-wrapper">
        <div className="title">
          <span>{t('任务列表')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className="search-wrapper" style={{ marginRight: 8 }}>
            <FilterFormSelect
              filterOptions={options}
              value={taskFormValue}
              onChange={newValue => {
                console.log('newValue', newValue)
                setTaskFormValue(newValue)
              }}
            />
          </div>
          <div className="reset">
            <Button
              icon={<SyncOutlined style={{ color: '#666' }} />}
              className="reset-btn"
              onClick={() => {
                initTableData(expandedRowKeys[0], 1, true)
                taskData.setCount(1)
              }}
            />
          </div>
        </div>
      </div>
      {/* 任务列表 */}
      <div className="table-wrapper">
        <TaskTable
          batchOperation={batchOperation}
          columns={columns}
          subColumns={subColumns}
          dataSource={taskData.dataSource}
          subDataSource={taskData.subDataSource}
          searchParams={searchParams}
          setSearchParams={(params: any) => setSearchParams(params)}
          total={taskData.total}
          loading={taskData.getTaskListLoading}
          hasMore={taskData.subTotal > searchParams.limit * taskData.count}
          loadMoreSubData={loadMoreSubData}
          subLoading={taskData.getTaskExecuteListLoading}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
          selectedSubRowKeys={selectedSubRowKeys}
          setSelectedSubRowKeys={setSelectedSubRowKeys}
          expandedRowKeys={expandedRowKeys}
          expandHandler={handleExpand}
          onTableChange={handleTableChange}
        />
      </div>
      {/* 详情抽屉 */}
      <DetailDrawer
        open={taskModals.view.visible}
        onClose={taskModals.closeView}
        id={taskModals.view.id}
      />
      {/* 删除任务弹窗 */}
      <DeleteModal
        text={
          taskModals.remove.type === 'main'
            ? t('是否确认删除所选任务（删除主任务时将同时删除其下的子任务）？')
            : t('此操作将永久删除，是否继续？')
        }
        open={taskModals.remove.visible}
        onOk={() => handleDelete()}
        onCancel={() => taskModals.setRemove(origin => ({ ...origin, visible: false, type: '' }))}
        loading={
          (taskModals.remove.type === 'main' && deleteTaskLoading) ||
          (taskModals.remove.type === 'sub' && deleteSubTaskLoading)
        }
      />
      {/* 创建报表弹窗 */}
      <CreateReport
        visible={taskModals.report.visible}
        multiple={taskModals.report.multiple}
        selectItem={taskModals.report.selectItem}
        createLoading={createLoading}
        templateType={11}
        onConfirm={e => {
          confirmAddGroup(e)
        }}
        onCancel={taskModals.closeReport}
      />
      {/* 任务复测弹窗 */}
      <RetestModal
        visible={taskModals.retestVisible.visible}
        onCancel={() => {
          taskModals.setRetestVisible({ visible: false, result_id: '', task_id: '' })
        }}
        onSuccess={() => {
          taskModals.setRetestVisible(origin => ({ ...origin, visible: false }))
          initTableData(taskModals.retestVisible.task_id, 1)
          taskData.setCount(1)
        }}
        id={taskModals.retestVisible.result_id}
        name={taskData.mainTaskName}
      />
    </Wrapper>
  )
}

export default TaskListMain

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background: #ffffff;
  box-shadow: 0px 4px 8px 0px rgba(95, 103, 129, 0.06);
  border-radius: 8px;
  padding: 12px 0;
  display: flex;
  flex-direction: column;

  .title-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 20px;
    margin-bottom: 6px;

    .title {
      font-weight: 600;
      font-size: 16px;
    }

    .reset-btn {
      width: 32px;
      height: 32px;
      background: #f1f3f8;
      border-radius: 6px;
      border: none;

      &:hover {
        background: #e6e9f0;
      }
    }
  }

  .table-wrapper {
    padding: 0 20px;
    overflow-y: scroll;
  }
`
